import { Component, OnInit, AfterViewInit, Renderer2, ElementRef, ViewChild, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { TOKEN, CURRENT_USER } from 'src/app/utils/items/storage-names';
import { ApiService } from 'src/app/utils/services/api.service';
import { getStorage } from 'src/app/utils/helper/storage';
import { DoctorService } from 'src/app/utils/services/doctor/doctor.service';
import { AppService } from 'src/app/utils/services/app.service';
import { environment } from 'src/environments/environment';
import { filter } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-menu-sidebar',
  templateUrl: './menu-sidebar.component.html',
  styleUrls: ['./menu-sidebar.component.scss']
})
export class MenuSidebarComponent implements OnInit, AfterViewInit {

  @ViewChild('mainSidebar', { static: false }) mainSidebar;

  @Output() mainSidebarHeight: EventEmitter<any> = new EventEmitter<any>();

  processing = false;
  user: any;
  subscription: any;
  selectedCompany: any = null;
  logoPath: string = 'assets/img/logo.png'; // Default logo
  logoIconPath: string = 'assets/img/mwell-MD-icon.png'; // Default icon
  companyStyles: any = {}; // New property to hold company-specific styles
  companies: any[] = []; // Add companies array

  constructor(
    private service: ApiService,
    public _appService: AppService,
    private router: Router,
    private doctorService: DoctorService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient // Add HttpClient injection
  ) { }

  ngOnInit() {
    this.user = getStorage(CURRENT_USER, true);
    this.subscription = this.user?.active_subscription;
    
    // Load companies data
    this.loadCompanies();
    
    this.getDoctor();
    this.loadSelectedCompany();

    // Listen for route changes to detect company changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      // Check if selected company has changed
      this.loadSelectedCompany();
    });
  }

  // Load companies data from JSON file
  loadCompanies(): void {
    this.http.get('./assets/data/companies.json').subscribe(
      (data: any[]) => {
        this.companies = data;
        console.log('Loaded companies data in menu sidebar:', this.companies);
        // Update selected company with full data if it exists
        this.loadSelectedCompany();
      },
      (error) => {
        console.error('Error loading companies data in menu sidebar:', error);
      }
    );
  }

  async getDoctor() {
    this.doctorService.getDoctorByUser(this.user?.id, { includes: 'prc_license,hmo,hospital_affiliations.hospital_master,clinics.resource.services.service_item,medical_associations' })
      .subscribe((response: any) => {
        this.user.doctor = response;

        // Handle is_onelife logic for menu sidebar
        this.handleIsOnelifeForSidebar(response);
      });
  }

  /**
   * Handle the is_onelife flag from doctor response for sidebar
   * @param doctorResponse The response from getDoctor API call
   */
  private handleIsOnelifeForSidebar(doctorResponse: any): void {
    const isOnelife = doctorResponse?.is_onelife ?? false;

    localStorage.setItem('is_onelife', JSON.stringify(isOnelife));

    const userSelectedCompany = localStorage.getItem('userSelectedCompany');

    if (isOnelife && !userSelectedCompany) {
      // Find company with ID 2 from companies data if available
      if (this.companies && this.companies.length > 0) {
        const onelifeCompany = this.companies.find(company => company.id === 2);
        if (onelifeCompany) {
          this.setDefaultCompanyFromData(onelifeCompany);
        } else {
          // Fallback to hardcoded data if company not found
          this.setDefaultCompany(2);
        }
      } else {
        // Fallback to hardcoded data if companies data not loaded
        this.setDefaultCompany(2);
      }
      // Trigger change detection immediately after setting company
      this.cdr.detectChanges();
    }
  }

  /**
   * Set default company in localStorage from company data
   * @param companyData The company data to set as default
   */
  private setDefaultCompanyFromData(companyData: any): void {
    localStorage.setItem('selectedCompany', JSON.stringify(companyData));

    // Update the component's selectedCompany immediately
    this.selectedCompany = companyData;

    this.updateLogoPaths(companyData);
    this.applyCompanyStyles(companyData);

    // Force change detection
    this.cdr.detectChanges();
  }

  /**
   * Set default company in localStorage
   * @param companyId The ID of the company to set as default
   */
  private setDefaultCompany(companyId: number): void {
    // Try to find the company in our companies data first
    if (this.companies && this.companies.length > 0) {
      const company = this.companies.find(c => c.id === companyId);
      if (company) {
        this.setDefaultCompanyFromData(company);
        return;
      }
    }
    
    // Fallback to hardcoded data if company not found or companies data not loaded
    const defaultCompany = {
      id: companyId,
      name: "1Life Healthcare Inc.",
      code: "2000",
      logoIcon: "logo2_icon.png",
      logo: "logo2.png",
      styles: {
        "margin-top": "30px",
        "margin-left": "15px"
      }
    };

    localStorage.setItem('selectedCompany', JSON.stringify(defaultCompany));

    // Update the component's selectedCompany immediately
    this.selectedCompany = defaultCompany;

    this.updateLogoPaths(defaultCompany);
    this.applyCompanyStyles(defaultCompany);

    // Force change detection
    this.cdr.detectChanges();
  }

  ngAfterViewInit() {
    this.mainSidebarHeight.emit(this.mainSidebar.nativeElement.offsetHeight);
  }

  // Load selected company from localStorage
  loadSelectedCompany() {
    const storedCompany = localStorage.getItem('selectedCompany');
    if (storedCompany) {
      try {
        const parsedCompany = JSON.parse(storedCompany);
        // Only update if the company has actually changed
        if (JSON.stringify(this.selectedCompany) !== storedCompany) {
          this.selectedCompany = parsedCompany;
          // Update logo paths based on selected company
          this.updateLogoPaths(this.selectedCompany);
          // Apply company-specific styles
          this.applyCompanyStyles(this.selectedCompany);
          // Trigger change detection to ensure the view updates immediately
          this.cdr.detectChanges();
        }
      } catch (e) {
        console.error('Error parsing selected company from localStorage:', e);
      }
    } else {
      // Check if is_onelife is true to set default company to ID 2
      const isOnelifeStr = localStorage.getItem('is_onelife');
      let isOnelife = false;
      if (isOnelifeStr !== null) {
        try {
          isOnelife = JSON.parse(isOnelifeStr);
        } catch (e) {
          console.error('Error parsing is_onelife from localStorage:', e);
        }
      }
      
      // If we have companies data, set default based on is_onelife flag
      if (this.companies && this.companies.length > 0) {
        let defaultCompany;
        if (isOnelife) {
          // Find company with ID 2 (1Life)
          defaultCompany = this.companies.find(company => company.id === 2);
        } else {
          // Find company with ID 1 (mWell)
          defaultCompany = this.companies.find(company => company.id === 1);
        }
        
        // Fallback to first company if neither is found
        defaultCompany = defaultCompany || this.companies[0];
        
        // Update component state
        this.selectedCompany = defaultCompany;
        this.updateLogoPaths(defaultCompany);
        this.applyCompanyStyles(defaultCompany);
        this.cdr.detectChanges();
        
        console.log('Set default company in menu sidebar based on is_onelife flag:', defaultCompany);
      }
    }
  }

  // Method to handle company updates from parent components
  updateSelectedCompany(company: any) {
    this.selectedCompany = company;
    // Update logo paths based on selected company
    this.updateLogoPaths(company);
    // Apply company-specific styles
    this.applyCompanyStyles(company);
    // Trigger change detection to ensure the view updates immediately
    this.cdr.detectChanges();
  }

  // Update logo paths based on company data
  updateLogoPaths(company: any) {
    if (company && company.logo) {
      this.logoPath = `assets/img/${company.logo}`;
    } else {
      this.logoPath = 'assets/img/logo.png'; // Default logo
    }

    if (company && company.logoIcon) {
      this.logoIconPath = `assets/img/${company.logoIcon}`;
    } else {
      this.logoIconPath = 'assets/img/mwell-MD-icon.png'; // Default icon
    }
  }

  // Apply company-specific styles
  applyCompanyStyles(company: any) {
    if (company && company.styles) {
      this.companyStyles = company.styles;
    } else {
      this.companyStyles = {}; // Default styles
    }
  }

  isCurrentPath(homeName) {
    const paths = window?.location?.pathname?.split('/')?.filter((x) => x);
    return paths[0] === homeName;
  }

  isCurrentSubpath(pathName: string): boolean {
    return window?.location?.pathname?.split('/')?.filter((x) => x)[1] === pathName;
  }

  switchToMwellBasic() {
    let url = environment.mwell_basic;

    //if (url.indexOf('platform') === -1) {
    url += '/onboard/dashboard';
    //}

    location.href = url;
  }

  logout() {
    if (this.processing) {
      return;
    }
    this.processing = true;

    this.service.logout()
      .subscribe((response: any) => {
        this.router.navigate(['/logout']);
      }, (err) => {
        this.processing = false;
      });
  }
}